import { Box, Card, Checkbox, Divider, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';

import { CellRenderParams, DataTableColumn, SelectionModel } from '../types';

interface DataTableCardProps<T extends Record<string, any> = any> {
    row: T;
    columns: DataTableColumn<T>[];
    rowIndex: number;
    selectionModel: SelectionModel;
    onRowSelect: (rowId: string | number) => void;
    checkboxSelection?: boolean;
    isRowSelectable?: (row: T) => boolean;
    onRowClick?: (row: T, event: React.MouseEvent) => void;
    onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
    getCellProps?: (params: { row: T; column: DataTableColumn<T>; rowIndex: number }) => Record<string, any>;
    dense?: boolean;
    collapsible?: boolean;
    collapseAnchorColumn?: string;
    collapsibleExpandedRows?: Set<string | number>;
    onToggleCollapsibleRow?: (rowId: string) => void;
}

/**
 * Renders a single data row as a mobile-friendly card.
 *
 * Column grouping based on sticky hints:
 *   - sticky: true  → top section (key identity columns)
 *   - stickyRight: true OR last column in config → bottom section (action column, no label)
 *   - everything else → middle section, label + value rows
 */
export const DataTableCard = <T extends Record<string, any> = any>({
    row,
    columns,
    rowIndex,
    selectionModel,
    onRowSelect,
    checkboxSelection = false,
    isRowSelectable,
    onRowClick,
    onRowDoubleClick,
    getCellProps,
    dense = true,
    collapsible = false,
    collapseAnchorColumn,
    collapsibleExpandedRows,
    onToggleCollapsibleRow,
}: DataTableCardProps<T>): React.ReactElement => {
    const theme = useTheme();

    const rowId: string | number = (row as any)._id ?? (row as any).id ?? rowIndex;
    const isSelected = selectionModel.selectedRows.includes(rowId);
    const selectable = isRowSelectable ? isRowSelectable(row) : true;

    // Determine if there are any stickyRight columns explicitly declared
    const hasStickyRight = columns.some((c) => c.stickyRight === true);

    // Partition columns into three sections
    const stickyColumns = columns.filter((c) => c.sticky === true && !c.stickyRight);
    const actionColumns = hasStickyRight
        ? columns.filter((c) => c.stickyRight === true)
        : columns.slice(-1); // fallback: last column
    const actionColumnIds = new Set(actionColumns.map((c) => c.id));
    const stickyColumnIds = new Set(stickyColumns.map((c) => c.id));
    const regularColumns = columns.filter(
        (c) => !stickyColumnIds.has(c.id) && !actionColumnIds.has(c.id)
    );

    const hasActions = actionColumns.length > 0;

    const renderCellValue = (column: DataTableColumn<T>, colIndex: number): React.ReactNode => {
        const value = column.field ? (row as any)[column.field as string] : undefined;
        const currentRowId = (row as any)._id || (row as any).id || rowIndex;
        const isCollapsibleExpanded =
            collapsible && collapsibleExpandedRows ? collapsibleExpandedRows.has(currentRowId) : false;
        const isCollapseAnchor = collapsible && column.id === collapseAnchorColumn;
        const cellParams: CellRenderParams<T> = {
            value,
            row,
            column,
            rowIndex,
            columnIndex: colIndex,
            ...(isCollapseAnchor && {
                isExpanded: isCollapsibleExpanded,
                toggleExpanded: () => onToggleCollapsibleRow?.(String(currentRowId)),
                expandedCount: collapsibleExpandedRows?.size || 0,
            }),
        };
        const extraProps = getCellProps ? getCellProps({ row, column, rowIndex }) : {};

        if (column.renderCell) {
            return <span {...extraProps}>{column.renderCell(cellParams)}</span>;
        }
        return (
            <Typography
                variant="body2"
                component="span"
                sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}
                {...extraProps}
            >
                {value !== undefined && value !== null ? String(value) : '—'}
            </Typography>
        );
    };

    const handleCardClick = (event: React.MouseEvent) => {
        // Don't trigger row click when clicking the checkbox itself
        if ((event.target as HTMLElement).closest('input[type="checkbox"]')) return;
        onRowClick?.(row, event);
    };

    const handleCardDoubleClick = (event: React.MouseEvent) => {
        if ((event.target as HTMLElement).closest('input[type="checkbox"]')) return;
        onRowDoubleClick?.(row, event);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onRowSelect(rowId);
    };

    const py = dense ? 1 : 1.5;
    const px = dense ? 1.5 : 2;

    return (
        <Card
            elevation={0}
            onClick={handleCardClick}
            onDoubleClick={handleCardDoubleClick}
            sx={{
                border: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.border.main}`,
                borderRadius: 2,
                backgroundColor: isSelected
                    ? alpha(theme.palette.primary.main, 0.06)
                    : theme.palette.background.paper,
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                    backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.1)
                        : theme.palette.action.hover,
                },
                overflow: 'visible',
            }}
        >
            {/* Top row: Checkbox + Sticky (identity) columns placed side by side */}
            {(checkboxSelection || stickyColumns.length > 0) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        px,
                        pt: py,
                        pb: regularColumns.length > 0 ? 0 : (hasActions ? 0.75 : py),
                    }}
                >
                    {checkboxSelection && (
                        <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={!selectable}
                                onChange={handleCheckboxChange}
                                sx={{ p: 0.5 }}
                            />
                        </Box>
                    )}
                    {stickyColumns.map((column, colIndex) => (
                        <Box
                            key={column.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            <Box>{renderCellValue(column, colIndex)}</Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Divider between top row and regular columns */}
            {(checkboxSelection || stickyColumns.length > 0) && regularColumns.length > 0 && (
                <Divider sx={{ mx: px, mt: 0.75 }} />
            )}

            {/* Card body — regular columns */}
            {regularColumns.length > 0 && (
                <Box sx={{ px, pt: 0.75, pb: hasActions ? 0 : py }}>
                    {/* Regular columns — label left, value right */}
                    {regularColumns.map((column, idx) => {
                        const colIndex = columns.indexOf(column);
                        return (
                            <Box
                                key={column.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    py: 0.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        flexShrink: 0,
                                        minWidth: '30%',
                                        maxWidth: '45%',
                                    }}
                                >
                                    {column.label}
                                </Typography>
                                <Box sx={{ textAlign: 'right', flex: 1 }}>
                                    {renderCellValue(column, colIndex)}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Action columns — bottom section, full width, after divider */}
            {hasActions && (
                <>
                    <Divider />
                    <Box
                        sx={{
                            px,
                            py: 0.75,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {actionColumns.map((column, idx) => {
                            const colIndex = columns.indexOf(column);
                            return (
                                <React.Fragment key={column.id}>
                                    {renderCellValue(column, colIndex)}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </>
            )}
        </Card>
    );
};

export default DataTableCard;
